import React from 'react';

import { Elements, PaymentElement } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';

import { Block } from '../common/Block.jsx';
import { TabSelectionDropDown } from '../common/Tab.jsx';
import Token from '../common/Token.jsx';
import Utilities from '../common/Utilities.jsx';

import Kit from '../layout/Kit.jsx';

import Input from './Input.jsx';
import Text from './Text.jsx';

function StripeElementsComponent(props) {
    const [currentState, setCurrentState] = React.useState('empty');
    const [loading, setLoading] = React.useState(true);
    const resetLoading = () => setLoading(false);

    React.useEffect(() => {
        if (props.onParseConfig) {
            props.onParseConfig(props.blockConfig);
        }
    }, []);

    let style = {};
    const layoutStyles = Block.mapLayoutStyleConfigPropsWithPageKit(style, props);
    let className = 'cont--stripe__elements';
    if (!props.inLayout) {
        className += ' cont--kit__content';
    }
    if (props.disabled) {
        className += ' form--loading';
    }
    let renderedContent = (
        <div className={className} style={layoutStyles.style}>
            {
                props.builder ?
                    <StripeElementsBuilderComponent {...props} showInputValues={currentState == 'sample'} />
                    :
                    <div style={loading ? { cursor: 'progress', opacity: 0.7, pointerEvents: 'none' } : undefined}>
                        <div>
                            <PaymentElement onReady={resetLoading} />
                        </div>
                    </div>
            }
        </div>
    );
    if (layoutStyles.containerStyle) {
        renderedContent = <div className="cont--kit__wrapper" style={layoutStyles.containerStyle}>{renderedContent}</div>
    }
    return (
        <>
            {
                props.builder &&
                <TabSelectionDropDown
                    label="INPUT STATE"
                    tab={currentState}
                    onBlockMove={props.onBlockMove}
                    onSelect={setCurrentState}>
                    <option value={'empty'}>Empty</option>
                    <option value={'sample'}>Sample Text</option>
                </TabSelectionDropDown>
            }
            {renderedContent}
        </>
    );
}

function withStripeElements(WrappedComponent) {
    return function (props) {
        const [options, setOptions] = React.useState({
            currency: 'usd',
            paymentMethodCreation: 'manual',
            paymentMethodTypes: ['card'],
            setup_future_usage: 'off_session',
            appearance: {
                rules: {},
                theme: 'flat',
                variables: {}
            }
        });
        const [stripePromise, setStripePromise] = React.useState(null);
        const _colorValue = (propValue) => `#${Utilities.rgbaToHex(propValue).hex}`;
        const _designStyle = (propValue) => {
            let designStyle = Utilities.styleFromString(Utilities.extractStyle(propValue));
            const typeStyle = Utilities.typeStyleFromString(propValue);
            if (!typeStyle) {
                return designStyle;
            }
            let kitTypeStyle = Kit.kit(config, typeStyle);
            if (!kitTypeStyle || !kitTypeStyle.design) {
                return designStyle;
            }
            kitTypeStyle = Utilities.styleFromString(Utilities.extractStyle(kitTypeStyle.design));
            if (!kitTypeStyle) {
                return designStyle;
            }
            Utilities.copyObject(designStyle, kitTypeStyle);

            // Clean style for what Stripe Elements supports
            if (kitTypeStyle.backgroundColor) {
                kitTypeStyle.backgroundColor = _colorValue(kitTypeStyle.backgroundColor);
            }
            if (kitTypeStyle.color) {
                kitTypeStyle.color = _colorValue(kitTypeStyle.color);
            }
            kitTypeStyle.fontStyle = undefined;
            kitTypeStyle.verticalAlign = undefined;
            return kitTypeStyle;
        }
        const _propValue = (configProps, propName) => {
            const prop = configProps[propName];
            if (prop == undefined) {
                return;
            }
            let propValue = prop;
            if (propValue.unit) {
                propValue = (prop.value || '').trim();
            }
            if (propValue.length == 0) {
                return;
            }
            if (prop.unit) {
                propValue = `${propValue}${prop.unit}`;
            }
            return propValue
        }
        const _addRuleValues = (options, selector, value) => _setRuleValue(options, selector, undefined, value);
        const _setRuleValue = (options, selector, name, value) => {
            if (!options.appearance.rules[selector]) {
                options.appearance.rules[selector] = {};
            }
            if (name) {
                options.appearance.rules[selector][name] = value;
            } else if (value) {
                if (options.appearance.rules[selector].fontSize == '0px') {
                    value.fontSize = '0px';
                    value.lineHeight = '0px';
                }
                options.appearance.rules[selector] = {
                    ...options.appearance.rules[selector],
                    ...value
                };
            }
        }
        const config = props.config || {};
        const handleStripeElementsConfig = (blockConfig) => {
            const _options = { ...options };
            if (blockConfig) {
                if (blockConfig.block) {
                    for (let propName in blockConfig.block) {
                        let propValue = _propValue(blockConfig.block, propName);
                        if (propValue == undefined) {
                            continue;
                        }
                        switch (propName) {
                            case 'iconCardCvcColor':
                            case 'iconChevronDownColor':
                                propValue = _colorValue(propValue);
                                break;
                            case 'gridColumnSpacing':
                                const value = parseInt(blockConfig.block[propName].value, 10);
                                if (!isNaN(value)) {
                                    _options.appearance.variables.tabSpacing = `${value / 2.0}${blockConfig.block[propName].unit}`;
                                }
                                break;
                        }
                        _options.appearance.variables[propName] = propValue;
                    }
                }
                if (config.colors && config.colors.bodyText) {
                    const bodyColor = _colorValue(config.colors.bodyText);
                    _options.appearance.variables.colorText = bodyColor;
                    _options.appearance.variables.colorTextPlaceholder = bodyColor;
                    _options.appearance.variables.colorTextSecondary = bodyColor;
                    _setRuleValue(_options, '.Label', 'color', bodyColor);
                    _addRuleValues(_options, '.Input', {
                        borderColor: bodyColor,
                        borderStyle: 'solid',
                        borderWidth: '1px',
                        color: bodyColor
                    })
                }
                if (blockConfig.label) {
                    for (let propName in blockConfig.label) {
                        const propValue = _propValue(blockConfig.label, propName);
                        if (propValue == undefined) {
                            continue;
                        }
                        switch (propName) {
                            case 'design':
                                _addRuleValues(_options, '.Label', _designStyle(propValue));
                                break;
                            case 'visible':
                                if (propValue == false) {
                                    _setRuleValue(_options, '.Label', 'fontSize', '0px');
                                    _setRuleValue(_options, '.Label', 'lineHeight', '0px');
                                    _setRuleValue(_options, '.Label', 'margin', '0');
                                    _setRuleValue(_options, '.Label', 'padding', '0');
                                }
                                break;
                            default:
                                _setRuleValue(_options, '.Label', propName, propValue);
                                break;
                        }
                    }
                }
                if (blockConfig.input) {
                    for (let propName in blockConfig.input) {
                        const propValue = _propValue(blockConfig.input, propName);
                        if (propValue == undefined) {
                            continue;
                        }
                        switch (propName) {
                            case 'borderBottomColor':
                            case 'borderLeftColor':
                            case 'borderRightColor':
                            case 'borderTopColor':
                                _setRuleValue(_options, '.Input', propName, _colorValue(propValue));
                                break;
                            case 'borderRadius':
                                _options.appearance.variables[propName] = propValue;
                                break;
                            case 'design': {
                                const style = _designStyle(propValue);
                                style.backgroundColor = 'transparent';
                                _addRuleValues(_options, '.Input', style);
                                break;
                            }
                            case 'paddingBottom':
                            case 'paddingLeft':
                            case 'paddingRight':
                            case 'paddingTop':
                                _setRuleValue(_options, '.Input', propName, propValue);
                                _setRuleValue(_options, '.Input--empty', propName, propValue);
                                break;
                            case 'placeholder': {
                                const style = _designStyle(propValue);
                                style.backgroundColor = 'transparent';
                                _addRuleValues(_options, '.Input::placeholder', style);
                                _addRuleValues(_options, '.Input--empty', style);
                                break;
                            }
                            default:
                                _setRuleValue(_options, '.Input', propName, propValue);
                                break;
                        }
                    }
                }
                _setRuleValue(_options, '.TermsText', 'lineHeight', 'inherit');
                if (blockConfig.terms) {
                    for (let propName in blockConfig.terms) {
                        const propValue = _propValue(blockConfig.terms, propName);
                        if (propValue == undefined) {
                            continue;
                        }
                        switch (propName) {
                            case 'design':
                                _addRuleValues(_options, '.TermsText', _designStyle(propValue));
                                break;
                            default:
                                _setRuleValue(_options, '.TermsText', propName, propValue);
                                break;
                        }
                    }
                }
                if (blockConfig.tab) {
                    _setRuleValue(_options, '.TabLabel--selected', 'fontWeight', 'normal');
                    _setRuleValue(_options, '.Tab:focus', 'outline', 'none');
                    _setRuleValue(_options, '.Tab--selected:focus', 'outline', 'none');
                    _setRuleValue(_options, '.Tab:focus', 'boxShadow', 'none');
                    _setRuleValue(_options, '.Tab--selected:focus', 'boxShadow', 'none');

                    // Defaults
                    if (props.config && props.config.colors) {
                        _setRuleValue(_options, '.Tab', 'backgroundColor', _colorValue(props.config.colors.bodyBackground));
                        _setRuleValue(_options, '.Tab--selected', 'backgroundColor', _colorValue(props.config.colors.accent));
                    }
                    for (let propName in blockConfig.tab) {
                        const propValue = _propValue(blockConfig.tab, propName);
                        if (propValue == undefined) {
                            continue;
                        }
                        switch (propName) {
                            case 'borderBottomColor':
                            case 'borderLeftColor':
                            case 'borderRightColor':
                            case 'borderTopColor':
                                _setRuleValue(_options, '.Tab', propName, _colorValue(propValue));
                                break;
                            case 'borderBottomColorSelected':
                            case 'borderLeftColorSelected':
                            case 'borderRightColorSelected':
                            case 'borderTopColorSelected':
                                _setRuleValue(_options, '.Tab--selected', propName.replace('Selected', ''), _colorValue(propValue));
                                break;
                            case 'borderBottomStyleSelected':
                            case 'borderBottomWidthSelected':
                            case 'borderLeftStyleSelected':
                            case 'borderLeftWidthSelected':
                            case 'borderRightStyleSelected':
                            case 'borderRightWidthSelected':
                            case 'borderTopStyleSelected':
                            case 'borderTopWidthSelected':
                                _setRuleValue(_options, '.Tab--selected', propName.replace('Selected', ''), propValue);
                                break;
                            case 'backgroundColorSelected':
                                _setRuleValue(_options, '.Tab--selected', 'backgroundColor', propValue);
                                break;
                            case 'design':
                                _addRuleValues(_options, '.TabLabel', _designStyle(propValue));
                                break;
                            case 'designSelected':
                                _addRuleValues(_options, '.TabLabel--selected', _designStyle(propValue));
                                break;
                            case 'iconColor': {
                                const color = _colorValue(propValue);
                                _setRuleValue(_options, '.TabIcon', 'fill', color);
                                _options.appearance.variables.tabIconHoverColor = color;
                                break;
                            }
                            case 'iconColorSelected': {
                                const color = _colorValue(propValue);
                                _setRuleValue(_options, '.TabIcon.TabIcon--selected', 'fill', color);
                                _setRuleValue(_options, '.TabIcon--selected:hover', 'fill', color);
                                break;
                            }
                            case 'iconSpacing':
                                _setRuleValue(_options, '.TabLabel', 'paddingTop', propValue);
                                break;
                            default:
                                _setRuleValue(_options, '.Tab', propName, propValue);
                                break;
                        }
                    }
                }
            }
            setOptions(_options);
        }
        const handleStripeInitialize = (cartData) => {
            if (stripePromise != null || props.builder) {
                return;
            }
            const cart = cartData || {};
            if (!cart.config) {
                return;
            }
            const config = props.config || {};
            const totals = cart.totals || {};
            const _options = { ...options };
            if (cart.config.isImmediateCheckout) {
                _options.mode = 'payment';
                let amount = 0;
                if (totals.shipping) {
                    amount += totals.shipping.value;
                }
                if (totals.subtotal) {
                    amount += totals.subtotal.value;
                }
                if (totals.tax) {
                    amount += totals.tax.value;
                }
                _options.amount = Math.round(amount * 100);
            } else {
                _options.mode = 'setup';
            }
            if (config.fonts && config.fonts.linkHref) {
                _options.fonts = [{ cssSrc: config.fonts.linkHref }];
            }
            if (cart.config.currencyCode) {
                _options.currency = cart.config.currencyCode;
            }
            let stripeConstructorOptions;
            if (cart.config.isStripeExpress) {
                _options.onBehalfOf = cart.config.stripeAccountId;
            } else {
                stripeConstructorOptions = { stripeAccount: cart.config.stripeAccountId };
            }
            setOptions(_options);
            setStripePromise(loadStripe(cart.config.stripePublishableKey, stripeConstructorOptions));
        }
        return (
            <>
                <Elements options={options} stripe={stripePromise}>
                    <WrappedComponent {...props} onStripeElementsConfig={handleStripeElementsConfig} onStripeInitialize={handleStripeInitialize} />
                </Elements>
            </>
        );
    }
};

// #region BUILDER

const StripeElementsBuilderComponentType = {
    CardNumber: 'cardNumber',
    Country: 'country',
    CVC: 'cvc',
    ExpirationDate: 'expDate',
    Zip: 'zip'
};
function StripeElementsBuilderComponent(props) {
    let baseBlockConfigInput = {};
    let baseBlockConfigLabel = {};
    let blockConfigTerms = {};
    let columnSpacing, iconCardCvcColor, iconChevronDownColor, labelsVisible;
    const style = { display: 'flex', flexDirection: 'column', pointerEvents: 'none' };
    if (props.blockConfig) {
        if (props.blockConfig.block) {
            if (props.blockConfig.block.gridRowSpacing && props.blockConfig.block.gridRowSpacing.value && props.blockConfig.block.gridRowSpacing.value.length > 0) {
                style.gap = `${props.blockConfig.block.gridRowSpacing.value}${props.blockConfig.block.gridRowSpacing.unit}`
            }
            if (props.blockConfig.block.gridColumnSpacing && props.blockConfig.block.gridColumnSpacing.value && props.blockConfig.block.gridColumnSpacing.value.length > 0) {
                columnSpacing = `${props.blockConfig.block.gridColumnSpacing.value}${props.blockConfig.block.gridColumnSpacing.unit}`
            }
            iconCardCvcColor = props.blockConfig.block.iconCardCvcColor;
            iconChevronDownColor = props.blockConfig.block.iconChevronDownColor;
        }
        if (props.blockConfig.input) {
            baseBlockConfigInput = {
                block: { ...props.blockConfig.input },
                layout: { ...props.blockConfig.input, width: { unit: '%', value: '100' } }
            };
        }
        if (props.blockConfig.label) {
            labelsVisible = props.blockConfig.label.visible;
            baseBlockConfigLabel = {
                block: { displayText: props.blockConfig.label.design },
                layout: { ...props.blockConfig.label }
            }
        }
        if (props.blockConfig.terms) {
            blockConfigTerms = {
                block: { displayText: props.blockConfig.terms.design },
                layout: { ...props.blockConfig.terms }
            }
        }
    }
    const empty = () => { }
    const handleInputProps = ({ _, type }, inputProps) => {
        inputProps.name = type;
        switch (type) {
            case StripeElementsBuilderComponentType.CardNumber:
                if (props.showInputValues) {
                    inputProps.value = '9876 5432 1987 6543';
                }
                break;
            case StripeElementsBuilderComponentType.Country:
                inputProps.value = 'United States';
                break;
            case StripeElementsBuilderComponentType.CVC:
                if (props.showInputValues) {
                    inputProps.value = '999';
                }
                break;
            case StripeElementsBuilderComponentType.ExpirationDate:
                if (props.showInputValues) {
                    let date = new Date();
                    date.setFullYear(date.getFullYear() + 4);
                    let month = date.getMonth();
                    if (month < 10) {
                        month = `0${month}`;
                    }
                    inputProps.value = `${month} / ${date.getFullYear()}`;
                }
                break;
            case StripeElementsBuilderComponentType.Zip:
                if (props.showInputValues) {
                    inputProps.value = '30327';
                }
                break;
        }
    }
    return (
        <div className='cont--stripe__elements' style={style}>
            <div style={{ display: 'flex', flexDirection: 'row', gap: columnSpacing, width: '100%' }}>
                <div style={{ display: 'flex', flexDirection: 'column', flex: 6 }}>
                    <StripeElementsBuilderTab
                        blockConfig={props.blockConfig && props.blockConfig.tab}
                        config={props.config}
                        display="Card"
                        selected={true} />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', flex: 6 }}>
                    <StripeElementsBuilderTab
                        blockConfig={props.blockConfig && props.blockConfig.tab}
                        config={props.config}
                        display="Alternate Method" />
                </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                {
                    labelsVisible &&
                    <StripeElementsBuilderLabel blockConfig={{ block: { ...baseBlockConfigLabel.block }, layout: { ...baseBlockConfigLabel.layout } }} value="Card number" />
                }
                <StripeElementsBuilderInput
                    blockConfig={{ block: { ...baseBlockConfigInput.block, type: StripeElementsBuilderComponentType.CardNumber }, layout: { ...baseBlockConfigInput.layout } }}
                    placeholder="1234 1234 1234 1234"
                    onChange={empty}
                    onInputProps={handleInputProps} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'row', gap: columnSpacing, width: '100%' }}>
                <div style={{ display: 'flex', flexDirection: 'column', flex: 6 }}>
                    {
                        labelsVisible &&
                        <StripeElementsBuilderLabel blockConfig={{ block: { ...baseBlockConfigLabel.block }, layout: { ...baseBlockConfigLabel.layout } }} value="Expiration date" />
                    }
                    <StripeElementsBuilderInput
                        blockConfig={{ block: { ...baseBlockConfigInput.block, type: StripeElementsBuilderComponentType.ExpirationDate }, layout: { ...baseBlockConfigInput.layout } }}
                        placeholder="MM / YY"
                        onChange={empty}
                        onInputProps={handleInputProps} />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', flex: 6 }}>
                    {
                        labelsVisible &&
                        <StripeElementsBuilderLabel blockConfig={{ block: { ...baseBlockConfigLabel.block }, layout: { ...baseBlockConfigLabel.layout } }} value="Security code" />
                    }
                    <StripeElementsBuilderInput
                        blockConfig={{ block: { ...baseBlockConfigInput.block, type: StripeElementsBuilderComponentType.CVC }, layout: { ...baseBlockConfigInput.layout } }}
                        placeholder="CVC"
                        onChange={empty}
                        onInputProps={handleInputProps}>
                        <span className="icon-card" style={{ color: iconCardCvcColor }} />
                    </StripeElementsBuilderInput>
                </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'row', gap: columnSpacing, width: '100%' }}>
                <div style={{ display: 'flex', flexDirection: 'column', flex: 6 }}>
                    {
                        labelsVisible &&
                        <StripeElementsBuilderLabel blockConfig={{ block: { ...baseBlockConfigLabel.block }, layout: { ...baseBlockConfigLabel.layout } }} value="Country" />
                    }
                    <StripeElementsBuilderInput
                        blockConfig={{ block: { ...baseBlockConfigInput.block, type: StripeElementsBuilderComponentType.Country }, layout: { ...baseBlockConfigInput.layout } }}
                        onChange={empty}
                        onInputProps={handleInputProps}>
                        <span className="icon-dropdown-arrow" style={{ color: iconChevronDownColor }} />
                    </StripeElementsBuilderInput>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', flex: 6 }}>
                    {
                        labelsVisible &&
                        <StripeElementsBuilderLabel blockConfig={{ block: { ...baseBlockConfigLabel.block }, layout: { ...baseBlockConfigLabel.layout } }} value="ZIP code" />
                    }
                    <StripeElementsBuilderInput
                        blockConfig={{ block: { ...baseBlockConfigInput.block, type: StripeElementsBuilderComponentType.Zip }, layout: { ...baseBlockConfigInput.layout } }}
                        placeholder="12345"
                        onChange={empty}
                        onInputProps={handleInputProps} />
                </div>
            </div>
            <StripeElementsBuilderLabel blockConfig={blockConfigTerms} value="By providing your card information, you allow [the company] to charge your card for future payments in accordance with their terms." />
        </div>
    )
}

function StripeElementsBuilderInput(props) {
    const blockConfig = props.blockConfig;
    if (blockConfig.block && blockConfig.block.placeholder) {
        blockConfig.block.placeholder = blockConfig.block.placeholder.replace(`{${Token.PlaceholderText}}`, props.placeholder);
    }
    let content = (
        <Input.Component {...props} blockConfig={blockConfig} disabled={true} />
    );
    if (props.children) {
        content = <div className="input--icon">{content}{props.children}</div>
    }
    return content;
}

function StripeElementsBuilderLabel(props) {
    const blockConfig = props.blockConfig;
    if (blockConfig.block && blockConfig.block.displayText) {
        blockConfig.block.displayText = blockConfig.block.displayText.replace(`{${Token.LabelText}}`, props.value);
    }
    return (
        <Text.Component {...props} blockConfig={blockConfig} />
    );
}

function StripeElementsBuilderTab(props) {
    const blockConfig = props.blockConfig;
    const style = {};
    let iconColor = '#000000';
    if (props.selected) {
        iconColor = '#ffffff';
        style.backgroundColor = props.config && props.config.colors && props.config.colors.accent;
    }
    let design, iconSpacing;
    if (blockConfig) {
        Block.mapStyleConfigProps(style, blockConfig, (propName, propValue, propUnit) => {
            switch (propName) {
                case 'backgroundColor':
                    if (!props.selected && propValue) {
                        style.backgroundColor = propValue;
                    }
                    return true;
                case 'backgroundColorSelected':
                    if (props.selected && propValue) {
                        style.backgroundColor = propValue;
                    }
                    return true;
                case 'borderBottomColor':
                case 'borderBottomStyle':
                case 'borderLeftColor':
                case 'borderLeftStyle':
                case 'borderRightColor':
                case 'borderRightStyle':
                case 'borderTopColor':
                case 'borderTopStyle':
                    if (!props.selected) {
                        style[propName] = propValue;
                    }
                    return true;
                case 'borderBottomWidth':
                case 'borderLeftWidth':
                case 'borderRightWidth':
                case 'borderTopWidth':
                    if (!props.selected && propValue) {
                        style[propName] = `${propValue}${propUnit}`;
                    }
                    return true;
                case 'borderBottomColorSelected':
                case 'borderBottomStyleSelected':
                case 'borderLeftColorSelected':
                case 'borderLeftStyleSelected':
                case 'borderRightColorSelected':
                case 'borderRightStyleSelected':
                case 'borderTopColorSelected':
                case 'borderTopStyleSelected':
                    if (props.selected) {
                        style[propName.replace('Selected', '')] = propValue;
                    }
                    return true;
                case 'borderBottomWidthSelected':
                case 'borderLeftWidthSelected':
                case 'borderRightWidthSelected':
                case 'borderTopWidthSelected':
                    if (props.selected && propValue) {
                        style[propName.replace('Selected', '')] = `${propValue}${propUnit}`;
                    }
                    return true;
                case 'design':
                    if (!props.selected) {
                        design = propValue;
                    }
                    return true;
                case 'designSelected':
                    if (props.selected) {
                        design = propValue;
                    }
                    return true;
                case 'iconColor':
                    if (!props.selected && propValue) {
                        iconColor = propValue;
                    }
                    return true;
                case 'iconColorSelected':
                    if (props.selected && propValue) {
                        iconColor = propValue;
                    }
                    return true;
                case 'iconSpacing':
                    if (propValue) {
                        iconSpacing = `${propValue}${propUnit}`;
                    }
                    return true;
            }
        })
    }
    const display = design && design.replace(`{${Token.LabelText}}`, props.display) || '';
    return (
        <div className="stripeButton" style={style}>
            <div className="icon">
                <span style={{ backgroundColor: iconColor }} />
                <span style={{ backgroundColor: iconColor }} />
            </div>
            <div className="richText" dangerouslySetInnerHTML={{ __html: display }} style={{ paddingTop: iconSpacing }} />
        </div>
    )
}

// #endregion BUILDER


export { StripeElementsComponent, withStripeElements };